/**
 * NapkinFormat — utility functions for the .napkin sketch JSON format.
 *
 * Schema:
 * {
 *   version: 1,
 *   created: ISO string,
 *   modified: ISO string,
 *   objects: [{ id, type: 'stroke', x: 0, y: 0, data: { points, color, size } }]
 * }
 */

let _counter = 0;
const uid = () => `s${Date.now().toString(36)}${(++_counter).toString(36)}`;

export function createNapkin() {
  const now = new Date().toISOString();
  return { version: 1, created: now, modified: now, objects: [] };
}

export function addStroke(napkin, points, color = '#1a1a1a', size = 2) {
  const stroke = {
    id: uid(),
    type: 'stroke',
    x: 0,
    y: 0,
    data: { points, color, size },
  };
  return {
    ...napkin,
    modified: new Date().toISOString(),
    objects: [...napkin.objects, stroke],
  };
}

export function removeStroke(napkin, strokeId) {
  return {
    ...napkin,
    modified: new Date().toISOString(),
    objects: napkin.objects.filter((o) => o.id !== strokeId),
  };
}

export function clearAll(napkin) {
  return { ...napkin, modified: new Date().toISOString(), objects: [] };
}

export function serialize(napkin) {
  return JSON.stringify({ ...napkin, modified: new Date().toISOString() });
}

export function deserialize(jsonString) {
  try {
    return JSON.parse(jsonString);
  } catch {
    return createNapkin();
  }
}

/**
 * Convert a point array to a smooth SVG path using quadratic bezier interpolation.
 * Each point: { x, y }
 */
export function pointsToSvgPath(points) {
  if (!points || points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x},${points[0].y}`;
  if (points.length === 2) {
    return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
  }

  // Start at first point
  let d = `M ${points[0].x},${points[0].y}`;

  // Midpoint between consecutive points as endpoints; actual points as control
  for (let i = 1; i < points.length - 1; i++) {
    const mx = (points[i].x + points[i + 1].x) / 2;
    const my = (points[i].y + points[i + 1].y) / 2;
    d += ` Q ${points[i].x},${points[i].y} ${mx},${my}`;
  }

  // End at last point
  const last = points[points.length - 1];
  d += ` L ${last.x},${last.y}`;

  return d;
}
